import { useEffect, useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

export default function Contact() {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://crownabundance.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    'name': 'Contact The CROWN Abundance Movement',
    'description': 'Contact The CROWN Abundance Movement for inquiries about membership, partnerships, media, or support.',
    'url': `${baseUrl}/contact`,
    'mainEntity': {
      '@type': 'Organization',
      'name': 'The CROWN Abundance Movement',
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': '+27-12-345-6789',
        'contactType': 'customer service',
        'email': 'info@nowabundance.com',
        'areaServed': 'ZA',
        'availableLanguage': 'English'
      },
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': '116 Limpopo Avenue, Doringkloof',
        'addressLocality': 'Centurion',
        'addressRegion': 'Gauteng',
        'postalCode': '0157',
        'addressCountry': 'ZA'
      }
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contact The CROWN Abundance Movement | Get in Touch | House of CROWN';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact The CROWN Abundance Movement for inquiries about membership, partnerships, media, or support. Connect with our team to begin your journey of wealth, relationship, and legacy transformation.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contact CROWN movement, membership inquiry, partnership opportunities, media inquiries, support contact, House of CROWN contact');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d4fjftse56aa2r6jvc7g', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          interest: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      content: 'info@nowabundance.com',
      link: 'mailto:info@nowabundance.com',
      gradient: 'from-amber-400 to-yellow-300',
      description: 'Send us an email anytime'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      content: '+27 (0) 12 345 6789',
      link: 'tel:+27123456789',
      gradient: 'from-emerald-400 to-teal-500',
      description: 'Mon-Fri from 9am to 6pm'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      content: '116 Limpopo Avenue, Doringkloof',
      link: '#',
      gradient: 'from-purple-400 to-indigo-500',
      description: 'Centurion, 0157, South Africa'
    }
  ];

  const interestAreas = [
    'Open Community Membership',
    'Global Family Membership',
    'Global Ambassadors Membership',
    'Wealth Mastery Charter',
    'Relationship Wholeness Charter',
    'Generational Legacy Charter',
    'Partnership Opportunities',
    'Media Inquiries',
    'General Inquiry'
  ];

  return (
    <>
      <SEOHead
        title="Contact The CROWN Abundance Movement | Get in Touch | House of CROWN"
        description="Contact The CROWN Abundance Movement for inquiries about membership, partnerships, media, or support. Connect with our team to begin your journey of wealth, relationship, and legacy transformation."
        keywords="contact CROWN movement, membership inquiry, partnership opportunities, media inquiries, support contact, House of CROWN contact"
        canonicalPath="/contact"
        type="ContactPage"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen" style={{ background: '#3D2C8D' }}>
        {/* Hero Section with Background Image */}
        <section className="relative py-32 overflow-hidden min-h-[500px] flex items-center">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/643c704137095b734c4078d8c8a6c4ff.jpg"
              alt="Contact CROWN Movement"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-purple-900/85 to-purple-900/70"></div>
          </div>

          {/* Animated background elements */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-amber-500/15 to-yellow-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-r from-amber-300/10 to-yellow-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="mb-8">
              <div className="inline-block p-4 rounded-full bg-gradient-to-r from-amber-400/20 to-yellow-300/20 backdrop-blur-sm border border-amber-400/30 mb-6 animate-bounce">
                <i className="ri-mail-send-line text-4xl" style={{ color: '#F5A623' }}></i>
              </div>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
              Get In
              <span className="block text-6xl lg:text-8xl bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            
            <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-12" style={{ color: '#FFFFFF' }}>
              Connect with The CROWN Abundance Movement. We're here to answer your questions and guide you on your journey to financial dominion, relational wholeness, and generational legacy.
            </p>
          </div>
        </section>

        {/* Contact Info Cards with Enhanced Design */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactInfo.map((info, index) => (
                <a
                  key={index}
                  href={info.link}
                  className="group relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-amber-400/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl cursor-pointer overflow-hidden"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Animated background gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${info.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <div className="relative">
                    <div className={`w-16 h-16 bg-gradient-to-r ${info.gradient} rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                      <i className={`${info.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-center" style={{ color: '#F5A623' }}>
                      {info.title}
                    </h3>
                    <p className="text-center font-semibold mb-2" style={{ color: '#FFFFFF' }}>
                      {info.content}
                    </p>
                    <p className="text-center text-sm" style={{ color: '#FFFFFF', opacity: 0.7 }}>
                      {info.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section with Side Image */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Form */}
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-amber-400/20 shadow-2xl">
                <div className="text-center mb-12">
                  <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                    Send Us a Message
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-6"></div>
                  <p className="text-lg" style={{ color: '#FFFFFF' }}>
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label htmlFor="name" className="block text-sm font-medium mb-2" style={{ color: '#F5A623' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/10 border border-amber-400/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300"
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="group">
                      <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#F5A623' }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/10 border border-amber-400/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="group">
                      <label htmlFor="phone" className="block text-sm font-medium mb-2" style={{ color: '#F5A623' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-white/10 border border-amber-400/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="group">
                      <label htmlFor="interest" className="block text-sm font-medium mb-2" style={{ color: '#F5A623' }}>
                        Area of Interest *
                      </label>
                      <div className="relative">
                        <select
                          id="interest"
                          name="interest"
                          value={formData.interest}
                          onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                          required
                          className="w-full px-4 py-3 pr-8 rounded-xl bg-white/10 border border-amber-400/30 text-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 appearance-none cursor-pointer"
                        >
                          <option value="" className="bg-purple-900">Select an option</option>
                          {interestAreas.map((area) => (
                            <option key={area} value={area} className="bg-purple-900">
                              {area}
                            </option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#F5A623' }}></i>
                      </div>
                    </div>
                  </div>

                  <div className="group">
                    <label htmlFor="subject" className="block text-sm font-medium mb-2" style={{ color: '#F5A623' }}>
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-amber-400/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div className="group">
                    <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: '#F5A623' }}>
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-amber-400/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell us more about your inquiry..."
                    ></textarea>
                    <div className="text-right text-sm mt-2" style={{ color: '#F5A623' }}>
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-emerald-500/20 border border-emerald-400/50 rounded-xl p-4 flex items-center animate-pulse">
                      <i className="ri-checkbox-circle-line text-2xl text-emerald-400 mr-3"></i>
                      <p className="text-emerald-400 font-medium">
                        Thank you! Your message has been sent successfully. We'll get back to you soon.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-500/20 border border-red-400/50 rounded-xl p-4 flex items-center">
                      <i className="ri-error-warning-line text-2xl text-red-400 mr-3"></i>
                      <p className="text-red-400 font-medium">
                        Oops! Something went wrong. Please try again.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill mr-2"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Side Image */}
              <div className="relative group hidden lg:block">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20African%20American%20woman%20in%20elegant%20business%20attire%20smiling%20warmly%20while%20working%20on%20laptop%20in%20modern%20luxury%20office%2C%20welcoming%20gesture%2C%20floor-to-ceiling%20windows%20with%20city%20view%2C%20golden%20hour%20lighting%2C%20deep%20purple%20and%20gold%20color%20palette%2C%20high-end%20corporate%20photography%2C%20customer%20service%20excellence%2C%20approachable%20and%20professional%20atmosphere&width=600&height=700&seq=contact-side-image&orientation=portrait"
                  alt="Contact Support"
                  className="relative rounded-3xl shadow-2xl w-full h-[700px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                
                {/* Floating contact icons */}
                <div className="absolute top-8 right-8 w-16 h-16 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                  <i className="ri-customer-service-2-line text-2xl text-white"></i>
                </div>
                <div className="absolute bottom-8 left-8 w-12 h-12 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg animate-pulse" style={{ animationDelay: '1s' }}>
                  <i className="ri-chat-smile-3-line text-xl text-white"></i>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Our Location
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full"></div>
            </div>
            
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-amber-400/20">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3593.1234567890123!2d28.1876543!3d-25.8234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e956e0000000000%3A0x0!2sDoringkloof%2C%20Centurion%2C%20South%20Africa!5e0!3m2!1sen!2sza!4v1704067200000!5m2!1sen!2sza"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="CROWN Movement Location"
              ></iframe>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-r from-purple-400/20 to-indigo-300/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-amber-400/20 shadow-2xl">
              <i className="ri-customer-service-2-line text-5xl mb-6" style={{ color: '#F5A623' }}></i>
              <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Ready to Begin Your Journey?
              </h3>
              <p className="text-xl mb-8" style={{ color: '#FFFFFF' }}>
                Join thousands of members transforming their wealth, relationships, and legacy
              </p>
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/membership')}
                className="bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <i className="ri-crown-line mr-2"></i>
                Explore Membership Options
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
